import { Injectable } from "@angular/core";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Layer from "@arcgis/core/layers/Layer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import Sublayer from "@arcgis/core/layers/support/Sublayer";
import WMSSublayer from "@arcgis/core/layers/support/WMSSublayer";
import TileLayer from "@arcgis/core/layers/TileLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import LayerView from "@arcgis/core/views/layers/LayerView";
import MapView from "@arcgis/core/views/MapView";
import { Observable, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { LayerType } from "~app/common/components/map/map.models";
@Injectable({
    providedIn: "root"
})
export class LayerService {

    toggleLayer(visible: boolean, view: MapView, layerId: string, sublayerId?: number, sublayerName?: string) {

        const featureLayer = this.getLayer(view, layerId);
        const layer = this.getLayer(view, layerId, sublayerId);
        if (!layer || sublayerId !== undefined && this.isSubLayersReadOnly(featureLayer as Layer)) {
            return;
        }
        if (visible) {
            this.setRelatedLayers(view, layer, layerId, sublayerId);
        }

        if (layer.type === LayerType.wms) {
            const wmsLayer = featureLayer as WMSLayer;
            wmsLayer.findSublayerByName(sublayerName).visible = visible;
        } else {
            layer.visible = visible;
        }
    }

    isSubLayersReadOnly(layer: Layer) {
        if(layer.type === LayerType.mapImage) {
            const mapImageLayer = layer as MapImageLayer;
            if (!mapImageLayer.capabilities) {
                return false;
            }
            return !mapImageLayer.capabilities.exportMap.supportsSublayerVisibility;
        } else {
            // Hur ska denna koden skrivas
            return false;
        }
    }

    setOpacity(layerId: string, opacity: number, view: MapView) {
        const layer = this.getLayer(view, layerId);
        if (!layer) {
            return;
        }
        layer.opacity = opacity;
    }

    getViewLayer(layerId: string, view: MapView, unsubscribe$: Subject<void>): Observable<LayerView> {
        const subject = new Subject<LayerView>();
        reactiveUtils.whenOnce(() => view.ready).then(async () => {
            subject.next(await this.getLayerViewWhenReady(view, layerId));
        }, (reason) => {
            console.error(reason);
        });

        this.onMapViewUpdated(view, unsubscribe$)
            .pipe(debounceTime(200))
            .pipe(takeUntil(unsubscribe$))
            .subscribe(
                {
                    next: async () => {
                        subject.next(await this.getLayerViewWhenReady(view, layerId));
                    },
                    error: (error) => {
                        console.error(error);
                    }
                }
            );
        return subject.asObservable();
    }

    private onMapViewUpdated(view: MapView, unsubscribe$: Subject<void>) {
        const subject = new Subject<void>();
        const watchHandle = reactiveUtils.watch(
            () => (view.updating),
            (updating) => {

                if (unsubscribe$.closed) {
                    watchHandle.remove();
                }
                if (!updating) {
                    subject.next();
                }
            });
        return subject;
    }

    private setRelatedLayers(view: MapView, layer: TileLayer | Sublayer | WMSLayer | WMSSublayer, layerId: string, sublayerId?: number) {
        // If sublayer, make sure all parents are visible
        if (sublayerId !== undefined) {
            const featureLayer = this.getLayer(view, layerId);
            if (!featureLayer.visible) {
                this.setFeatureLevelVisibility(view, layerId);
            }
            this.ensureVisible(layer);
        }
    }

    private async getLayerViewWhenReady(view: MapView, layerId: string): Promise<LayerView> {
        const layer = view.map.layers.find(l => l.id === layerId);
        if (!layer) {
            return null;
        } else if (layer.loadStatus === "failed") {
            return undefined;
        }
        return view.whenLayerView(layer);
    }

    // When setting a featurelayer to true, arcgis automaticly sets all sublayers to visible whichs isnt always right.
    private setFeatureLevelVisibility(view: MapView, layerId: string) {
        const featureLayer = this.getLayer(view, layerId);
        featureLayer.visible = true;
        featureLayer.sublayers.forEach(sl => sl.visible = false);
    }

    private ensureVisible(layer: any) {
        let current = layer.parent;
        while (current !== undefined) {
            current.visible = true;
            current = current.parent;
        }
    }

    private getLayer(view: MapView, layerId: string, sublayerId?: number) {
        const layer = view.allLayerViews.find(l => l.layer.id === layerId)?.layer;
        if (layer?.type === LayerType.wms) {
            const wmsLayer = layer as WMSLayer;
            return wmsLayer;
        }

        const tileLayer = layer as TileLayer;
        if (sublayerId === undefined) {
            return tileLayer;
        }
        return tileLayer.allSublayers.find(s => s.id === sublayerId);
    }
}
